import { motion } from "framer-motion";
import { ArrowUpRight, Check, Smartphone, Wifi, Leaf, SignalHigh, Tag, HeartHandshake, UserCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "./Nav";

const TELECOM_IMG =
  "https://images.unsplash.com/photo-1696797804535-9e1792b7170e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG9yYW5nZSUyMGZpYmVyJTIwbmV0d29ya3xlbnwwfHx8fDE3ODc5MjExMjZ8MA&ixlib=rb-4.1.0&q=85";
const ENERGY_IMG =
  "https://images.unsplash.com/photo-1619697101606-7c092a8e6281?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGVuZXJneSUyMHdpbmQlMjB0dXJiaW5lJTIwbW9kZXJufGVufDB8fHx8MTc4NzkyMTEyNnww&ixlib=rb-4.1.0&q=85";

const icons = [Smartphone, Wifi, Leaf];
const whyIcons = [SignalHigh, Tag, HeartHandshake, UserCheck];

export const Services = () => {
  const { t } = useLang();
  const s = t.services;

  return (
    <section id="services" className="py-24 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-6"
        >
          {s.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-3xl mb-16 md:mb-20"
        >
          {s.title}
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="group md:col-span-2 md:row-span-2 relative overflow-hidden border border-white/10 hover:border-[#FF7900]/60 transition-colors duration-500 min-h-[480px] md:min-h-[640px] flex flex-col justify-end"
            data-testid="service-card-orange"
          >
            <img
              src={TELECOM_IMG}
              alt="Orange Belgium telecom"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative p-8 md:p-12">
              <span className="inline-block text-xs uppercase tracking-[0.2em] bg-[#FF7900] text-white px-4 py-1.5 rounded-full mb-6">
                {s.orange.tag}
              </span>
              <h3 className="font-display text-3xl md:text-5xl font-medium tracking-tight mb-4">{s.orange.title}</h3>
              <p className="text-white/70 max-w-lg leading-relaxed mb-6">{s.orange.desc}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {s.orange.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-white/80">
                    <Check className="w-4 h-4 text-[#FF7900] shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToId("contact")}
                data-testid="service-orange-cta"
                className="group/btn inline-flex items-center gap-2 text-sm font-semibold text-[#FF7900] hover:text-white transition-colors duration-300"
              >
                {s.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden border border-white/10 hover:border-[#FF7900]/60 transition-colors duration-500 min-h-[300px] flex flex-col justify-end"
            data-testid="service-card-eneco"
          >
            <img
              src={ENERGY_IMG}
              alt="Eneco energy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative p-8">
              <span className="inline-block text-xs uppercase tracking-[0.2em] bg-white text-black px-4 py-1.5 rounded-full mb-4">
                {s.eneco.tag}
              </span>
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-3">{s.eneco.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed mb-5">{s.eneco.desc}</p>
              <button
                onClick={() => scrollToId("contact")}
                data-testid="service-eneco-cta"
                className="group/btn inline-flex items-center gap-2 text-sm font-semibold text-[#FF7900] hover:text-white transition-colors duration-300"
              >
                {s.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </div>
          </motion.div>

          {s.small.map((card, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group border border-white/10 hover:border-[#FF7900]/60 bg-[#111111] p-8 flex flex-col gap-4 transition-colors duration-500"
                data-testid={`service-card-small-${i}`}
              >
                <span className="w-12 h-12 border border-white/15 flex items-center justify-center group-hover:bg-[#FF7900] group-hover:border-[#FF7900] transition-colors duration-500">
                  <Icon className="w-5 h-5 text-[#FF7900] group-hover:text-white transition-colors duration-500" />
                </span>
                <h3 className="font-display text-xl font-medium">{card.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{card.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-20 border border-[#FF7900]/30 bg-[#FF7900]/[0.04]"
          data-testid="why-orange-block"
        >
          <div className="px-8 md:px-12 pt-8 md:pt-10">
            <h3 className="font-display text-base md:text-lg font-semibold">{s.whyOrange.title}</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mt-6 md:mt-8 border-t border-white/10">
            {s.whyOrange.points.map((p, i) => {
              const Icon = whyIcons[i];
              return (
                <div key={p} className="bg-[#0d0d0d] p-6 md:p-8 flex flex-col gap-4" data-testid={`why-orange-point-${i}`}>
                  <Icon className="w-5 h-5 text-[#FF7900]" />
                  <p className="text-sm text-white/75 leading-relaxed">{p}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
